import type { ChecklistCatalogDeliveryBaseline } from './checklist-catalog-delivery';

/** P2a is measured dry-run evidence, never approval or Apply authority. */
export const CHECKLIST_CATALOG_IMPACT_POLICY = 'P2A_ORDINARY_V1' as const;
export const CHECKLIST_CATALOG_IMPACT_LIMITS = Object.freeze({
  maxRawBytes: 8 * 1024 * 1024,
  maxRoster: 10000,
  maxReferences: 10000,
  maxDefinitionsPerKind: 2000,
  maxOverlays: 2000,
  maxImportLedger: 200,
  queryPageSize: 500,
  maxCommands: 64,
  maxDatabaseBytes: 16 * 1024 * 1024,
  maxMilliseconds: 12000,
  maxReportBytes: 256 * 1024,
  maxReasons: 50,
});
export const CHECKLIST_CATALOG_IMPACT_ZIP_LIMITS = Object.freeze({
  maxArchiveBytes: 20 * 1024 * 1024,
  maxMembers: 10,
  maxMemberBytes: 8 * 1024 * 1024,
  maxExpandedBytes: 80 * 1024 * 1024,
});
export interface ChecklistCatalogImpactReason {
  code: string;
  /** Fixed allowlisted explanation, never a raw input/SDK error. */
  detail: string;
  fieldPath?: string;
  sourceItemId?: string;
  sourceSubsetId?: string;
  definitionId?: string;
}
export interface ChecklistCatalogImpactCoverage {
  catalog: 'COMPLETE' | 'PARTIAL' | 'NOT_RUN';
  corrections: 'COMPLETE' | 'PARTIAL' | 'NOT_RUN';
  savedReferences: 'UNKNOWN';
}
export interface ChecklistCatalogImpactBaselineDto {
  schemaVersion: 1;
  sourceSystem: 'azure-sql-ebay';
  sourceTargetId: string;
  observedAt: string;
  policyVersion: typeof CHECKLIST_CATALOG_IMPACT_POLICY;
  /** Null when a complete bounded baseline cannot be established. */
  basedOn: ChecklistCatalogDeliveryBaseline | null;
  coverage: ChecklistCatalogImpactCoverage;
  eligible: false;
  reasons: ChecklistCatalogImpactReason[];
}
export interface ChecklistCatalogImpactCounts {
  roster: number | null;
  parallelDefinitions: number | null;
  subsetDefinitions: number | null;
}
export interface ChecklistCatalogImpactDiffCounts {
  adds: number | null;
  updates: number | null;
  unchanged: number | null;
  sourceMissing: number | null;
  sourceMissingDisposition: 'RETAINED';
}
export interface ChecklistCatalogImpactDiff {
  roster: ChecklistCatalogImpactDiffCounts;
  parallelDefinitions: ChecklistCatalogImpactDiffCounts;
  subsetDefinitions: ChecklistCatalogImpactDiffCounts;
}
export interface ChecklistCatalogImpactDependencyCoverage {
  authoritativeReferences: { coverage: 'UNKNOWN'; count: null };
  draftSelections: { coverage: 'UNKNOWN'; count: null };
  reviewChecklistSelections: { coverage: 'UNKNOWN'; count: null };
  selectedDefinitions: { coverage: 'UNKNOWN'; count: null };
}
export interface ChecklistCatalogImpactReport {
  schemaVersion: 1;
  reportType: 'CATALOG_DRY_RUN';
  policyVersion: typeof CHECKLIST_CATALOG_IMPACT_POLICY;
  reportId: string;
  /** SHA-256 of canonical report contents excluding this digest field. */
  digest: string;
  deliveryId: string;
  manifestDigest: string;
  sourceSystem: 'azure-sql-ebay';
  sourceTargetId: string;
  execution: 'COMPLETED' | 'FAILED';
  catalogComparison: 'COMPLETE' | 'PARTIAL' | 'NOT_RUN';
  eligibility: 'BLOCKED';
  applyAllowed: false;
  verification: 'NOT_RUN';
  input: {
    transportSha256: string;
    rawSha256: string;
    transportBytes: number;
    rawBytes: number;
    claimedCounts: ChecklistCatalogImpactCounts;
    computedCounts: ChecklistCatalogImpactCounts;
    claimedFingerprint: string;
    computedFingerprint: string | null;
  };
  baseline: {
    /** Canonical SHA-256 of the private original claim; never echo arbitrary claim strings. */
    claimedDigest: string;
    observed: ChecklistCatalogDeliveryBaseline | null;
    checks: Array<{
      field: keyof ChecklistCatalogDeliveryBaseline;
      result: 'MATCH' | 'MISMATCH' | 'UNVERIFIED';
    }>;
  };
  coverage: ChecklistCatalogImpactCoverage;
  providerDiff: ChecklistCatalogImpactDiff;
  effectiveDiff: ChecklistCatalogImpactDiff;
  correctionImpact: {
    coverage: 'COMPLETE' | 'PARTIAL' | 'NOT_RUN';
    active: number | null;
    replayed: number | null;
    conflicts: number | null;
  };
  dependencyCoverage: ChecklistCatalogImpactDependencyCoverage;
  reasons: ChecklistCatalogImpactReason[];
  totalReasonCount: number;
  reasonsTruncated: boolean;
  bounds: typeof CHECKLIST_CATALOG_IMPACT_LIMITS;
  work: { commands: number; databaseBytes: number; elapsedMilliseconds: number };
  createdAt: string;
  expiresAt: string;
}
export interface ChecklistCatalogImpactReportDto {
  report: ChecklistCatalogImpactReport;
  /** Known catalog/correction fences only; saved references remain UNKNOWN. */
  freshness: {
    status: 'CURRENT' | 'STALE' | 'UNKNOWN';
    checkedAt: string;
    reasons: ChecklistCatalogImpactReason[];
  };
}
export type ChecklistCatalogImpactAction =
  | { action: 'baseline'; sourceTargetId: string }
  | { action: 'validate'; deliveryId: string; sourceTargetId: string; requestId: string }
  | { action: 'report'; deliveryId: string; sourceTargetId: string; reportId?: string };
